import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';
import { UXGenieTemplateRenderer } from './DynamicJSXRenderer';
import { HtmlTemplateFrame } from './UxgenieTemplateContentView';
import { decompressedData } from '../utils/commonutils';

export const STORAGE_KEY_PREFIX = 'templatePreview-';

/**
 * Check if a string is HTML content (document or fragment), not React JSX.
 * Mirrors `isHtmlContent` in `src/app/uxgenie/utils` — duplicated here since this
 * component lives in the shared lib, which can't reach into the app's own utils.
 */
const isHtmlContent = (value: unknown): boolean => {
    if (typeof value !== 'string') return false;
    const trimmed = value.trim();
    if (!trimmed) return false;

    if (/<!DOCTYPE\s+html/i.test(trimmed) || /^<html[\s>]/i.test(trimmed)) return true;

    if (!/<[a-z][a-z0-9]*[\s>/]/i.test(trimmed)) return false;

    const hasHtmlClass = /\bclass\s*=/i.test(trimmed);
    const hasJsxClass = /\bclassName\s*=/i.test(trimmed);
    if (hasHtmlClass && !hasJsxClass) return true;

    return /<(html|head|body|style|script|title|meta|link)\b/i.test(trimmed);
};

type TemplatePayload = {
    generatedCode: string;
    receivedClaimData: any;
    agentName?: string;
};

/**
 * Full-page template preview opened in a new tab.
 *
 * Data flow:
 *  - Web flow:       data is in sessionStorage before the tab opens (set by preOpened.sessionStorage)
 *  - Extension flow: data arrives via chrome.scripting.executeScript bridge after page load,
 *                    so we poll sessionStorage briefly until it appears.
 */
export const TemplatePreviewPage: React.FC = () => {
    const { t } = useTranslation();
    const [searchParams] = useSearchParams();
    const tabKey = searchParams.get('tabKey');

    const [payload, setPayload] = useState<TemplatePayload | null>(null);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        if (!tabKey) {
            setIsLoading(false);
            return;
        }

        let cancelled = false;
        let attempts = 0;
        const MAX_ATTEMPTS = 25; // 5 s at 200 ms intervals

        const tryRead = () => {
            if (cancelled) return;
            try {
                const stored = sessionStorage.getItem(`${STORAGE_KEY_PREFIX}${tabKey}`);
                if (stored) {
                    const decompressed = decompressedData(JSON.parse(stored)) as TemplatePayload;
                    if (!cancelled) {
                        setPayload(decompressed);
                        setIsLoading(false);
                    }
                    return;
                }
            } catch {
                // malformed data — stop polling
                if (!cancelled) setIsLoading(false);
                return;
            }

            if (attempts < MAX_ATTEMPTS) {
                attempts++;
                setTimeout(tryRead, 200);
            } else {
                if (!cancelled) setIsLoading(false); // give up, show error
            }
        };

        tryRead();
        return () => { cancelled = true; };
    }, [tabKey]);

    if (!tabKey) {
        return (
            <div className="flex flex-col items-center justify-center min-h-screen bg-default-100 p-4">
                <Icon icon="mdi:alert-circle-outline" className="text-default-400 mb-4" width={48} />
                <p className="text-default-500 text-center mb-4">
                    {t('Template preview data is missing or expired. Please open the template again from the claim view.')}
                </p>
            </div>
        );
    }

    if (isLoading) {
        return (
            <div className="flex flex-col items-center justify-center min-h-screen bg-default-100 p-4">
                <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin mb-4" />
                <p className="text-default-500">{t('Loading template...')}</p>
            </div>
        );
    }

    if (!payload?.generatedCode || !payload?.receivedClaimData) {
        return (
            <div className="flex flex-col items-center justify-center min-h-screen bg-default-100 p-4">
                <Icon icon="mdi:file-document-remove-outline" className="text-default-400 mb-4" width={48} />
                <p className="text-default-500 text-center mb-4">
                    {t('Template or claim data is missing.')}
                </p>
            </div>
        );
    }

    const { generatedCode, receivedClaimData } = payload;
    const isHtml = isHtmlContent(generatedCode);

    return (
        <div className="flex flex-col bg-default-100">
            <div className="overflow-auto p-2">
                <div className="bg-content1 p-2 w-full max-w-full">
                    {isHtml ? (
                        <HtmlTemplateFrame
                            code={generatedCode}
                            data={receivedClaimData}
                            className="w-full min-h-[400px] border-0 bg-white"
                        />
                    ) : (
                        <UXGenieTemplateRenderer
                            code={generatedCode}
                            data={receivedClaimData}
                            containerClassName="w-full max-w-full"
                        />
                    )}
                </div>
            </div>
        </div>
    );
};

export default TemplatePreviewPage;
